/* Core & libraries */
import React, { useState, useEffect, useMemo } from "react";
import { FormattedMessage } from "react-intl";
import { useTheme } from "react-jss";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import parse from "html-react-parser";

/* Helpers, constants, custom methods, internal libraries */
import { useAirlineProgramsService } from "../../services/airlineProgramsService.js";
import { useAnalytics } from "../../hooks/useAnalytics.js";
import { PAGE_TITLES } from "../../constants/appConstants.js";
import { AIRLINEPROGRAMS_DELTAAIRLINES_ROUTE, AIRLINEPROGRAMS_AMERICANAIRLINES_ROUTE, AIRLINEPROGRAMS_ALASKAAIRLINES_ROUTE, AIRLINEPROGRAMS_UNITEDAIRLINES_ROUTE } from "../../constants/routes.js";
import { useNotificationContext } from "../../contexts/notificationContext";

/* Components */
import { ReactComponent as UnitedAirlinesIconSvg } from "../../resources/images/united-airlines-icon.svg";
import { ReactComponent as DeltaAirlinesIconSvg } from "../../resources/images/delta-airlines-icon.svg";
import { ReactComponent as AmericanAirlinesIconSvg } from "../../resources/images/american-airlines-icon.svg";
import { ReactComponent as AlaskaAirlinesIconSvg } from "../../resources/images/alaska-airlines-icon.svg";
import AirlineProgramsTabSkeleton from "./skeletonAirlineProgramsTab/index.js";
import UCButton from "../buttons";

/* Styles */
import styles from "./styles.js";

const AirlineProgramsContent = ({ actionsDisabled, id }) => {
  const theme = useTheme();
  const classes = styles({ theme });

  const { getStaticContent, staticContent, getAirlineProgramsData, airlineProgramsData } = useAirlineProgramsService();
  const { initAnalytics } = useAnalytics();
  const { setNotification } = useNotificationContext();

  useEffect(() => {
    const initUtagData = {
      channel: 'content',
      pageName: PAGE_TITLES["airlinePrograms"]
    };

    initAnalytics(initUtagData, 'init call', 'view');

    getStaticContent();

    id ? getAirlineProgramsData(id) : getAirlineProgramsData();

    if (document.referrer.includes("/onboarding/traveler-registration")) {
      setNotification(
        "success",
        <FormattedMessage id="notifications.join.AA.successTitle" />,
        <FormattedMessage id="notifications.join.AA.successText" />
      );
    }
  }, [id]);

  const handleAirlineProgramRoute = airlineCode => {
    let url;
    let icon;
    switch (airlineCode) {
      case "DL":
        url = AIRLINEPROGRAMS_DELTAAIRLINES_ROUTE;
        icon = DeltaAirlinesIconSvg
        break;
      case "AA":
        url = AIRLINEPROGRAMS_AMERICANAIRLINES_ROUTE;
        icon = AmericanAirlinesIconSvg
        break;
      case "AS":
        url = AIRLINEPROGRAMS_ALASKAAIRLINES_ROUTE;
        icon = AlaskaAirlinesIconSvg
        break;
      case "UA":
        url = AIRLINEPROGRAMS_UNITEDAIRLINES_ROUTE;
        icon = UnitedAirlinesIconSvg
        break;
      default:
        url = undefined;
    }

    if (url) {
      window.location.href = url; // Navigate to the URL
    }
  }

  const handleAirlineIconDisplay = airlineCode => {
    switch (airlineCode) {
      case "DL":
        return <DeltaAirlinesIconSvg />;
      case "AA":
        return <AmericanAirlinesIconSvg />;
      case "AS":
        return <AlaskaAirlinesIconSvg />;
      case "UA":
        return <UnitedAirlinesIconSvg />;
      default:
        return null; // Return null if no matching icon is found
    }
  };

  const dataUnavailable = Object.keys(airlineProgramsData).length === 0;
  const staticContentUnavailable = Object.keys(staticContent).length === 0;
  const joinContentUnavailable = staticContent?.joinPage?.headerText === "" || staticContent?.joinPage?.text === ""

  const renderFormattedText = (text) => {
    return text
      .split("\n\n")
      .map((paragraph, idx) => (
        <div
          key={idx}
          className={
            idx === text.split("\n\n").length - 1
              ? classes.customContentTextLastParagraph
              : classes.customContentTextParagraph
          }
        >
          {parse(paragraph)}
        </div>
      ));
  };

  const airlineProgramsDataSorted = useMemo(() => {
    return airlineProgramsData?.suppliers?.slice().sort((a, b) => {
      const providerA = (a.providerName || "").toString().trim();
      const providerB = (b.providerName || "").toString().trim();
      return providerA.localeCompare(providerB, undefined, { sensitivity: 'base' });
    });
  }, [airlineProgramsData?.suppliers]);

  return (
    <div className={classes.airlineProgramsTab}>
      {!staticContentUnavailable && !joinContentUnavailable && staticContent !== null && (
        <div className={classes.customContentContainer}>
          <div>
            <div className={classes.customContentHeader}>
              {staticContent?.joinPage?.headerText}
            </div>
            <div className={classes.customContentText}>
              {renderFormattedText(staticContent?.joinPage?.text)}
            </div>
          </div>
        </div>
      )}
      <div className={classes.details}>
        <div>
          <div className={classes.title}>
            <FormattedMessage id="join.airlinePrograms.title" />
          </div>
          <div className={classes.description}>
            <FormattedMessage id="join.airlinePrograms.description" />
          </div>
        </div>
      </div>
      <div className={classes.airlineProgramsContent}>
        {dataUnavailable ? (
          <AirlineProgramsTabSkeleton />
        ) : (
          <>
            {airlineProgramsDataSorted?.map((airline) => {
              return (
                <div className={classes.airlineProgramsCard} key={airline?.providerCode}>
                  <div className={classes.logoDiv}>
                    {handleAirlineIconDisplay(airline.providerCode)}
                    <div>
                      <Col>
                        <p className={classes.logoTxt}>{airline.providerName}</p>
                        <p className={classes.logoTxtDescription}><FormattedMessage id={`join.airlinePrograms.${airline.providerCode}.airlineDescription`} /></p>
                      </Col>
                    </div>
                  </div>

                  {airline.userStatus !== "ACTIVE" && (
                    <UCButton
                      onClick={() => handleAirlineProgramRoute(airline.providerCode)}
                      type="default"
                      className={classes.cancel}
                      text={<FormattedMessage id="join.airlinePrograms.linkText" />}
                      disabled={actionsDisabled}
                    />
                  )}

                  {airline.userStatus === "ACTIVE" && (
                    <div className={classes.buttonsContainer}>
                      <Col xs={6} className={classes.connected}>
                        <FormattedMessage id="join.airlinePrograms.connectedText" />
                      </Col>
                      <Col xs={6} className={classes.manage}>
                        <UCButton
                          onClick={() => handleAirlineProgramRoute(airline.providerCode)}
                          type="defaultLink"
                          className={actionsDisabled && classes.manageDisabledBtn}
                          text={<FormattedMessage id="join.airlinePrograms.manageText" />}
                        />
                      </Col>
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )}
      </div>
    </div>
  );
};

export default AirlineProgramsContent;
